import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

const ToastContext = createContext(null);

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const toastTimers = useRef(new Map());
  const isActive = useRef(true);

  // Cleanup function on unmount
  useEffect(() => {
    return () => {
      isActive.current = false;
      toastTimers.current.forEach(timer => clearTimeout(timer));
      toastTimers.current.clear();
    };
  }, []);

  // Add toast function with duplicate prevention
  const addToast = useCallback(({ type, title, description, id }) => {
    try {
      // Generate truly unique IDs with a combination of timestamp and random string
      const toastId = id || `toast-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      
      // Clear existing timer if toast is being updated
      if (toastTimers.current.has(toastId)) {
        clearTimeout(toastTimers.current.get(toastId));
      }

      // Add new toast
      setToasts(prevToasts => {
        // Check if toast with same content already exists
        const existingToast = prevToasts.find(t => 
          t.type === type && 
          t.title === title && 
          t.description === description &&
          Date.now() - t.timestamp < 5000 // Within last 5 seconds
        );

        if (existingToast) return prevToasts; // Skip if duplicate

        const newToast = {
          id: toastId,
          type,
          title,
          description,
          timestamp: Date.now()
        };

        // Set timer to remove toast
        const timer = setTimeout(() => {
          if (isActive.current) {
            removeToast(toastId);
          }
          toastTimers.current.delete(toastId);
        }, 5000);

        toastTimers.current.set(toastId, timer);

        // Important: Always create a new array for state updates
        return [...prevToasts, newToast];
      });
    } catch (error) {
      console.error('Error adding toast:', error);
    }
  }, []);

  // Remove toast by ID
  const removeToast = useCallback((id) => {
    try {
      if (!isActive.current) return;
      
      setToasts(prevToasts => {
        // Important: Create a new array without the removed toast
        const updatedToasts = prevToasts.filter(toast => toast.id !== id);
        return updatedToasts;
      });
      
      // Clear timer if exists
      if (toastTimers.current.has(id)) {
        clearTimeout(toastTimers.current.get(id));
        toastTimers.current.delete(id);
      }
    } catch (error) {
      console.error('Error removing toast:', error);
    }
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};